import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTickerSearch } from '../../hooks/useTickerSearch';
import { useAuth } from '../../contexts/AuthContext';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getTextSizeClass, getSubsectionHeadingSizeClass } from '../../utils/fontSizeUtils';
import { userTickerListApi, UserTickerList } from '../../services/userTickerListApi';
import { usageLimitsApi } from '../../services/usageLimitsApi';
import UpgradeDialog from '../UpgradeDialog/UpgradeDialog';
import NewListDialog from '../NewListDialog/NewListDialog';

export interface StockManagementControlsProps {
  // List Management
  listName: string;
  isEditingTickers: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onDoneEdit: () => void;
  
  // Stock Management
  tickerList: string[];
  onAddTicker: (ticker: string) => void;
  onSave: () => void;
  
  // Optional: Custom list name editing
  tempListName?: string;
  onTempListNameChange?: (name: string) => void;
  
  // Optional: User list management (when logged in)
  userLists?: UserTickerList[];
  currentListId?: number;
  onListChange?: (listId: number) => void;
  onCreateNewList?: () => void;
  onCreateNewListWithName?: (listName: string) => Promise<void>;
  
  // Optional: Delete list functionality
  onDeleteList?: () => void;
  showDeleteButton?: boolean;
  
  // Optional: Share list functionality
  onShareList?: () => void;
  showShareButton?: boolean;
  
  // Optional: Refresh usage limits callback
  onRefreshUsageLimits?: (refreshFn: () => void) => void;
  
  // Optional: Public list mode (read-only)
  isPublicList?: boolean;
  
  // Optional: Show promotional tooltip for new users from Get Started page
  showPromotionalTooltip?: boolean;
}

const StockManagementControls: React.FC<StockManagementControlsProps> = ({
  listName,
  isEditingTickers,
  onStartEdit,
  onCancelEdit,
  onDoneEdit,
  tickerList,
  onAddTicker,
  onSave,
  tempListName = '',
  onTempListNameChange,
  userLists = [],
  currentListId,
  onListChange,
  onCreateNewList,
  onCreateNewListWithName,
  onDeleteList,
  showDeleteButton = false,
  onShareList,
  showShareButton = false,
  onRefreshUsageLimits,
  isPublicList = false,
  showPromotionalTooltip = false,
}) => {
  const { isAuthenticated, user } = useAuth();
  const { fontSize } = useFontSize();
  
  // Local state for dropdown and search
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAddingStock, setIsAddingStock] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ tooltipLeft: number; arrowLeft: number } | null>(null);
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const [upgradeDialogType, setUpgradeDialogType] = useState<'list' | 'ticker' | 'signup' | 'list-ticker-limit'>('list');
  const [canCreateList, setCanCreateList] = useState(true);
  const [canAddTicker, setCanAddTicker] = useState(true);
  const [isNewListDialogOpen, setIsNewListDialogOpen] = useState(false);
  
  // Refs
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const addStockButtonRef = useRef<HTMLButtonElement | null>(null);
  
  // Calculate tooltip position helper function
  const calculateTooltipPosition = useCallback(() => {
    if (!addStockButtonRef.current) return null;
    
    const button = addStockButtonRef.current;
    const buttonLeft = button.offsetLeft;
    const buttonWidth = button.offsetWidth;
    const buttonCenter = buttonLeft + buttonWidth / 2;
    
    const isMobile = window.innerWidth < 640; // sm breakpoint
    // Tooltip width must match the conditional classes: authenticated uses narrower width
    const tooltipWidth = isMobile
      ? (isAuthenticated ? 196 : 280)
      : (isAuthenticated ? 350 : 500);
    
    let tooltipLeft: number;
    let arrowLeft: number;
    if (isMobile) {
      // On small screens the button shows "+ Stock" (narrower). Center tooltip under button
      // so the arrow always aligns with the button center regardless of button width.
      tooltipLeft = buttonCenter - tooltipWidth / 2 - 50;
      arrowLeft = tooltipWidth / 2 + 30;
    } else {
      // Medium/large screens: original positioning (Save button present for unauthenticated)
      tooltipLeft = -110;
      arrowLeft = buttonCenter - tooltipLeft;
    }
    
    return { tooltipLeft, arrowLeft };
  }, [isAuthenticated]);

  useEffect(() => {
    if (showPromotionalTooltip && tickerList.length === 0 && !isPublicList) {
      const position = calculateTooltipPosition();
      if (position) {
        setIsTooltipVisible(true);
        setTooltipPosition(position);
      } else {
        setIsTooltipVisible(false);
        setTooltipPosition(null);
      }
    } else {
      setIsTooltipVisible(false);
      setTooltipPosition(null);
    }
  }, [showPromotionalTooltip, tickerList.length, isPublicList, isAuthenticated, calculateTooltipPosition]);

  // Recalculate position on window resize
  useEffect(() => {
    if (!isTooltipVisible) return;
    
    const handleResize = () => {
      const position = calculateTooltipPosition();
      if (position) {
        setTooltipPosition(position);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isTooltipVisible, calculateTooltipPosition]);
  
  // Dismiss tooltip when clicking anywhere on the page
  useEffect(() => {
    if (!isTooltipVisible) return;
    
    const handleClick = () => {
      setIsTooltipVisible(false);
    };
    
    // Add a small delay to avoid dismissing immediately when tooltip appears
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClick);
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClick);
    };
  }, [isTooltipVisible]);
  
  // Ticker search functionality
  const {
    searchTerm,
    results,
    loading: searchLoading,
    showResults,
    handleSearchChange,
    clearSearch
  } = useTickerSearch();

  // Sort user lists alphabetically by list name
  const sortedUserLists = useMemo(() => {
    return [...userLists].sort((a, b) => {
      const nameA = a.list_name.toLowerCase();
      const nameB = b.list_name.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [userLists]);

  // Determine which lists are locked (beyond 3rd position for free users)
  // Free users can only access the first 3 lists
  const lockedListIds = useMemo(() => {
    if (isAuthenticated && !user?.hasProSubscription && sortedUserLists.length > 3) {
      // Lists beyond the 3rd position (index 3+) are locked
      return sortedUserLists.slice(3).map(list => list.id);
    }
    return [];
  }, [sortedUserLists, isAuthenticated, user?.hasProSubscription]);

  // Check if the current list is locked
  const isCurrentListLocked = useMemo(() => {
    return currentListId !== null && currentListId !== undefined && lockedListIds.includes(currentListId);
  }, [currentListId, lockedListIds]);

  // Function to refresh usage limits
  const refreshUsageLimits = async () => {
    if (isAuthenticated) {
      try {
        const [listLimit, tickerLimit] = await Promise.all([
          usageLimitsApi.checkListLimit(),
          usageLimitsApi.checkTickerLimit()
        ]);
        setCanCreateList(listLimit.canCreateList);
        setCanAddTicker(tickerLimit.canAddTicker);
      } catch (error) {
        console.error('Failed to check usage limits:', error);
        // Default to allowing actions if check fails
        setCanCreateList(true);
        setCanAddTicker(true);
      }
    }
  };

  // Check usage limits when component mounts
  useEffect(() => {
    refreshUsageLimits();
  }, [isAuthenticated]);

  // Expose refresh function to parent component
  useEffect(() => {
    if (onRefreshUsageLimits) {
      onRefreshUsageLimits(refreshUsageLimits);
    }
  }, [onRefreshUsageLimits]);

  // Handle clicking the "+ Add Stock" button - check limits before showing input
  const handleAddStockClick = () => {
    // Dismiss tooltip when clicking Add Stock button
    setIsTooltipVisible(false);
    
    // Check if the current list is locked first (for free users)
    if (isAuthenticated && !user?.hasProSubscription && isCurrentListLocked) {
      setUpgradeDialogType('list');
      setIsUpgradeDialogOpen(true);
      return;
    }
    
    // Check per-list ticker limit
    if (isAuthenticated) {
      // For authenticated users, check per-list limit based on subscription
      const perListLimit = user?.hasProSubscription ? 20 : 10;
      if (tickerList.length >= perListLimit) {
        setUpgradeDialogType('list-ticker-limit');
        setIsUpgradeDialogOpen(true);
        return;
      }
    } else {
      // For unauthenticated users, check if they have 10 tickers
      if (tickerList.length >= 10) {
        setUpgradeDialogType('signup');
        setIsUpgradeDialogOpen(true);
        return;
      }
    }
    
    // If limits are okay, show the input box
    setIsAddingStock(true);
  };

  // Handle adding a ticker
  const handleAddTicker = async (ticker: string) => {
    // Check unique ticker limit (for authenticated users)
    // Note: Per-list limit is already checked when button is clicked
    if (isAuthenticated && !canAddTicker) {
      // Dismiss the auto-complete menu first
      clearSearch();
      setIsAddingStock(false);
      setUpgradeDialogType('ticker');
      setIsUpgradeDialogOpen(true);
      return;
    }
    
    onAddTicker(ticker);
    setIsAddingStock(false);
    clearSearch();
    
    // Recheck usage limits after adding a ticker (for authenticated users)
    if (isAuthenticated) {
      try {
        const tickerLimit = await usageLimitsApi.checkTickerLimit();
        setCanAddTicker(tickerLimit.canAddTicker);
      } catch (error) {
        console.error('Failed to recheck ticker limit:', error);
      }
    }
  };

  // Handle creating a new list
  const handleCreateNewList = () => {
    if (!isAuthenticated) {
      // For unauthenticated users, redirect to signup
      onCreateNewList?.();
      return;
    }
    
    if (!canCreateList) {
      setUpgradeDialogType('list');
      setIsUpgradeDialogOpen(true);
      return;
    }
    
    // For authenticated users, show dialog to enter list name
    setIsNewListDialogOpen(true);
  };

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsAddingStock(false);
        clearSearch();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [clearSearch]);

  return (
    <>
      {/* LEFT SIDE - List management */}
      <div className="flex items-center gap-6">
        {!isEditingTickers ? (
          isPublicList ? (
            // Public list: show label only (no dropdown)
            <div className={`text-gray-900 dark:text-text-primary ${getSubsectionHeadingSizeClass(fontSize)} font-semibold`}>
              {listName}
            </div>
          ) : (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-2 text-gray-900 dark:text-text-primary bg-white dark:bg-border-color border border-border-light dark:border-border-color px-3 py-1.5 rounded-md ${getTextSizeClass(fontSize)} font-medium hover:bg-gray-50 dark:hover:bg-opacity-80 transition-colors max-w-48 sm:max-w-none`}
              >
                <span className="truncate">{listName}</span>
                <svg className="w-4 h-4 text-gray-900 dark:text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
                </svg>
              </button>
            {isDropdownOpen && (
              <div className="absolute left-0 mt-2 w-48 bg-white dark:bg-background border border-border-light dark:border-border-color rounded-md shadow-lg z-[60] max-h-60 overflow-y-auto">
                {isAuthenticated && sortedUserLists.length > 0 ? (
                  <>
                    {sortedUserLists.map((list, index) => {
                      const isLocked = lockedListIds.includes(list.id);
                      return (
                        <button
                          key={list.id}
                          className={`block w-full text-left px-3 py-1.5 ${getTextSizeClass(fontSize)} transition-colors ${
                            currentListId === list.id 
                              ? 'text-gray-900 dark:text-text-primary bg-highlight-gold/10 dark:bg-highlight-gold/20 font-semibold' 
                              : 'text-gray-700 dark:text-text-primary hover:bg-gray-100 dark:hover:bg-border-color'
                          }`}
                          onClick={() => {
                            onListChange?.(list.id);
                            setIsDropdownOpen(false);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            {isLocked && (
                              <svg className="w-4 h-4 text-gray-700 dark:text-text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                              </svg>
                            )}
                            <span className="truncate">{list.list_name}</span>
                          </div>
                        </button>
                      );
                    })}
                    <div className="border-t border-border-light dark:border-border-color my-1"></div>
                  </>
                ) : null}
                <button
                  className={`block w-full text-left px-3 py-1.5 ${getTextSizeClass(fontSize)} transition-colors text-gray-700 dark:text-text-primary hover:bg-gray-100 dark:hover:bg-border-color`}
                  onClick={() => {
                    if (!canCreateList && isAuthenticated) {
                      // Show warning dialog for locked option
                      setIsDropdownOpen(false);
                      setUpgradeDialogType('list');
                      setIsUpgradeDialogOpen(true);
                    } else {
                      handleCreateNewList();
                      setIsDropdownOpen(false);
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    {!canCreateList && isAuthenticated && (
                      <svg className="w-4 h-4 text-gray-700 dark:text-text-primary" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                    )}
                    <span>{!canCreateList && isAuthenticated ? 'New List' : '+ New List'}</span>
                  </div>
                </button>
              </div>
            )}
            </div>
          )
        ) : (
          <input
            type="text"
            value={tempListName}
            onChange={(e) => onTempListNameChange?.(e.target.value)}
            className={`bg-transparent border border-highlight-gold rounded-md py-2 px-4 text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)} font-medium focus:outline-none focus:ring-2 focus:ring-highlight-gold focus:border-transparent transition-all duration-200 placeholder-text-light-secondary dark:placeholder-text-secondary w-32 sm:w-40 md:w-48`}
            placeholder="Enter list name"
            autoFocus
          />
        )}

        {!isEditingTickers && !isPublicList ? (
          <div className="flex items-center gap-4">
            <button
              onClick={onStartEdit}
              className="transition-colors duration-200 text-gray-900 dark:text-text-secondary hover:text-gold-text-light dark:hover:text-highlight-gold"
              title="Edit tickers"
            >
              <svg className="lucide lucide-pencil" fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
                <path d="m15 5 4 4"></path>
              </svg>
            </button>
            {showShareButton && onShareList && (
              <button
                onClick={onShareList}
                className="transition-colors duration-200 text-gray-900 dark:text-text-secondary hover:text-blue-600 dark:hover:text-blue-400"
                title="Share list"
              >
                <svg className="lucide lucide-share-2" fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="18" cy="5" r="3"></circle>
                  <circle cx="6" cy="12" r="3"></circle>
                  <circle cx="18" cy="19" r="3"></circle>
                  <line x1="8.59" x2="15.42" y1="13.51" y2="17.49"></line>
                  <line x1="15.41" x2="8.59" y1="6.51" y2="10.49"></line>
                </svg>
              </button>
            )}
            {showDeleteButton && onDeleteList && (
              <button
                onClick={onDeleteList}
                className="transition-colors duration-200 text-gray-900 dark:text-text-secondary hover:text-red-600"
                title="Delete list"
              >
                <svg className="lucide lucide-trash-2" fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 6h18"></path>
                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                  <line x1="10" x2="10" y1="11" y2="17"></line>
                  <line x1="14" x2="14" y1="11" y2="17"></line>
                </svg>
              </button>
            )}
          </div>
        ) : (
          !isPublicList && (
            <div className="flex items-center gap-4">
              <button
                onClick={onCancelEdit}
                className={`text-text-light-secondary dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors duration-200 ${getTextSizeClass(fontSize)} font-medium`}
              >
                Cancel
              </button>
              <button
                onClick={onDoneEdit}
                className={`bg-primary text-gray-900 px-3 py-1.5 rounded-md ${getTextSizeClass(fontSize)} font-medium hover:bg-opacity-90 transition-colors duration-200`}
              >
                Done
              </button>
            </div>
          )
        )}
      </div>

      {/* RIGHT SIDE - Stock management */}
      {!isEditingTickers && !isPublicList && (
        <div className="relative flex items-center space-x-4">
          {!isAddingStock ? (
            <button
              ref={addStockButtonRef}
              onClick={handleAddStockClick}
              className="flex items-center gap-2 text-gray-900 dark:text-text-secondary hover:text-gold-text-light dark:hover:text-highlight-gold transition-colors duration-200"
            >
              <svg fill="currentColor" height="20" viewBox="0 0 256 256" width="20" xmlns="http://www.w3.org/2000/svg">
                <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
              </svg>
              <span className={`font-medium ${getTextSizeClass(fontSize)}`}>
                <span className="hidden sm:inline">Add Stock</span>
                <span className="sm:hidden">Stock</span>
              </span>
            </button>
          ) : (
            <div ref={searchRef} className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Enter ticker or name"
                className={`bg-transparent border border-highlight-gold rounded-md py-2 px-4 text-text-light-primary dark:text-text-primary ${getTextSizeClass(fontSize)} font-medium focus:outline-none focus:ring-2 focus:ring-highlight-gold focus:border-transparent transition-all duration-200 w-32 sm:w-48 placeholder-text-light-secondary dark:placeholder-text-secondary`}
                autoFocus
              />
              {showResults && results.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-surface-light dark:bg-background-card border border-border-light dark:border-border-color rounded-md shadow-lg z-[60] max-h-60 overflow-y-auto">
                  {results.map((result, index) => (
                    <button
                      key={index}
                      onClick={() => handleAddTicker(result.ticker)}
                      className={`w-full px-4 py-2 text-left ${getTextSizeClass(fontSize)} text-text-light-primary dark:text-text-primary hover:bg-gray-100 dark:hover:bg-border-color transition-colors duration-200`}
                    >
                      <div className="font-medium">{result.ticker}</div>
                      <div className="text-text-light-secondary dark:text-text-secondary text-xs">{result.name}</div>
                    </button>
                  ))}
                </div>
              )}
              {searchLoading && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-surface-light dark:bg-background-card border border-border-light dark:border-border-color rounded-md shadow-lg z-50 p-4">
                  <div className={`flex items-center gap-2 text-text-light-secondary dark:text-text-secondary ${getTextSizeClass(fontSize)}`}>
                    <div className="animate-spin w-4 h-4 border border-highlight-gold border-t-transparent rounded-full"></div>
                    <span>Searching...</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Only show Save button for unauthenticated users */}
          {!isAuthenticated && (
            <button 
              onClick={onSave}
              className="flex items-center gap-2 text-gray-900 dark:text-text-secondary hover:text-gold-text-light dark:hover:text-highlight-gold transition-colors duration-200"
            >
              <svg fill="currentColor" height="20" viewBox="0 0 256 256" width="20" xmlns="http://www.w3.org/2000/svg">
                <path d="M219.31,80,176,36.69A15.86,15.86,0,0,0,164.69,32H48A16,16,0,0,0,32,48V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V91.31A15.86,15.86,0,0,0,219.31,80ZM168,208H88V152h80Zm40,0H184V152a16,16,0,0,0-16-16H88a16,16,0,0,0-16,16v56H48V48H164.69L208,91.31ZM160,72a8,8,0,0,1-8,8H96a8,8,0,0,1-16,0h56A8,8,0,0,1,160,72Z"></path>
              </svg>
              <span className={`font-medium ${getTextSizeClass(fontSize)} hidden sm:inline`}>Save</span>
            </button>
          )}
          
          {/* Promotional tooltip - absolutely positioned overlay */}
          {isTooltipVisible && tooltipPosition && (
            <div 
              className="absolute top-full mt-2 z-[9999] pointer-events-none"
              style={{
                left: `${tooltipPosition.tooltipLeft}px`
              }}
            >
              <div className={`bg-background-card dark:bg-gray-700 border border-border-color dark:border-gray-600 rounded-lg shadow-lg px-4 py-3 relative pointer-events-auto ${isAuthenticated ? 'max-w-[196px] sm:w-[350px]' : 'max-w-[280px] sm:w-[500px]'}`}>
                {/* Arrow pointing up to the middle of "+ Add Stock" button */}
                <div 
                  className="absolute -top-2 w-0 h-0"
                  style={{
                    left: `${tooltipPosition.arrowLeft}px`
                  }}
                >
                  <div className="border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent border-b-background-card dark:border-b-gray-700"></div>
                </div>
              
                <div className="flex items-start gap-3">
                  {/* Lightbulb icon */}
                  <div className="flex-shrink-0 w-10 h-10 bg-primary dark:bg-highlight-gold rounded-full flex items-center justify-center relative">
                    <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  
                  {/* Text content */}
                  <div className="flex-1">
                    <div className={`font-bold text-white mb-1 ${getTextSizeClass(fontSize)}`}>
                      Start here!
                    </div>
                    <div className={`text-white text-sm ${getTextSizeClass(fontSize)}`}>
                      Click here to add your first stock.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      <UpgradeDialog
        isOpen={isUpgradeDialogOpen}
        onClose={() => setIsUpgradeDialogOpen(false)}
        title={
          upgradeDialogType === 'list'
            ? 'List Limit Reached'
            : upgradeDialogType === 'ticker'
            ? 'Ticker Limit Reached'
            : upgradeDialogType === 'list-ticker-limit'
            ? 'List Ticker Limit Reached'
            : 'Sign Up Required'
        }
        message={
          upgradeDialogType === 'list'
            ? (user?.hasProSubscription
                ? 'You have reached the maximum number of lists (10) allowed for the Pro Plan.'
                : isCurrentListLocked
                ? 'This list is locked because you have more than 3 lists on the free plan. Please upgrade to add more tickers to locked lists.'
                : 'You have reached the maximum number of lists (3) for your current plan. Please upgrade to create more lists.')
            : upgradeDialogType === 'ticker'
            ? (user?.hasProSubscription
                ? 'You have reached the maximum number of unique tickers (100) allowed for the Pro Plan.'
                : 'You have reached the maximum number of unique tickers (20) for your current plan. Please upgrade to add more tickers.')
            : upgradeDialogType === 'list-ticker-limit'
            ? (user?.hasProSubscription
                ? 'You have reached the maximum number of tickers (20) allowed per list for the Pro Plan.'
                : 'You have reached the maximum number of tickers (10) per list for your current plan. Please upgrade to add more tickers to this list.')
            : 'You have reached the maximum number of tickers (10) for users without an account. Please sign up for an account to add more tickers.'
        }
        type={upgradeDialogType === 'list-ticker-limit' ? 'ticker' : upgradeDialogType}
        hideButtons={(upgradeDialogType === 'list' && user?.hasProSubscription) || 
                     (upgradeDialogType === 'ticker' && user?.hasProSubscription) ||
                     (upgradeDialogType === 'list-ticker-limit' && user?.hasProSubscription)}
      />
      
      {/* New List Dialog */}
      {onCreateNewListWithName && (
        <NewListDialog
          isOpen={isNewListDialogOpen}
          onClose={() => setIsNewListDialogOpen(false)}
          onCreateList={onCreateNewListWithName}
          existingLists={userLists}
        />
      )}
    </>
  );
};

export default StockManagementControls;
